import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/formularios.css';
import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveApiBaseList } from '../utils/apiBase';

export default function ModificarGrupo() {
  // Selección por grado (único control que decide el grupo)
  const [gradosDisponibles, setGradosDisponibles] = useState([]); // [{grado, etiqueta}]
  const [gradoSel, setGradoSel] = useState(''); // grado vigente seleccionado
  const [grupo, setGrupo] = useState(null);      // {IdGrupo, Grado, Status, CicloEscolar}
  const [status, setStatus] = useState('Activo');
  const [alumnos, setAlumnos] = useState([]);    // [{id,nombre,curp}]
  const [cargando, setCargando] = useState(false);

  // Buscador inteligente para AGREGAR alumnos
  const [inputAlumno, setInputAlumno] = useState('');
  const [sugerencias, setSugerencias] = useState([]);  // [{IdAlumno, NombreCompleto, CURP}]
  const [loadingSug, setLoadingSug] = useState(false);
  const debounceRef = useRef(null);

  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          const data = trimmed ? JSON.parse(trimmed) : {};
          return { data, url };
        }

        lastError = new Error(`Respuesta inválida desde ${url}`);
        console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const sendJsonWithFallback = useCallback(async (path, payload, method = 'POST') => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // keep raw message
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la solicitud ${method} a ${url}:`, error);
          continue;
        }

        const trimmed = text.trim();
        if (trimmed && trimmed.startsWith('<')) {
          const error = new Error(`Respuesta inválida desde ${url}`);
          lastError = error;
          console.warn(`⚠️ Contenido no válido desde ${url}`);
          continue;
        }

        if (!trimmed) return { text: '', parsed: null, url };

        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            return { text: parsed?.mensaje || parsed?.message || trimmed, parsed, url };
          } catch (parseErr) {
            console.warn(`⚠️ No se pudo parsear respuesta JSON desde ${url}:`, parseErr);
          }
        }

        return { text: trimmed, parsed: null, url };
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al enviar ${method} a ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const navigate = useNavigate();
    const location = useLocation();
    const handleSalir = () => {
      const rol = resolveRolBase(location.pathname);
      if (rol) {
        navigate(`/${rol}/inicio`, { replace: true });
      } else {
        // Fallback por si no se detecta rol ni en path ni en localStorage:
        navigate('/inicio', { replace: true });
      }
    };

  const cargarGradosRegistrados = useCallback(async () => {
    try {
      const { data } = await fetchJsonWithFallback('/grupos');
      const lista = Array.isArray(data) ? data : [];

      const vistos = new Set();
      const opciones = [];
      lista.forEach((g) => {
        const grado = g.Grado ?? g.grado ?? null;
        if (!grado || vistos.has(grado)) return;
        vistos.add(grado);
        const etiquetas = {
          1: 'Primero',
          2: 'Segundo',
          3: 'Tercero',
          4: 'Cuarto',
          5: 'Quinto',
          6: 'Sexto',
        };
        opciones.push({
          grado: String(grado),
          etiqueta: etiquetas[grado] || `Grado ${grado}`,
        });
      });

      setGradosDisponibles(opciones);
      setGradoSel((prev) => {
        if (prev && opciones.some((op) => op.grado === prev)) return prev;
        return opciones.length > 0 ? opciones[0].grado : '';
      });
    } catch (err) {
      console.error('Error al cargar grupos registrados:', err);
      setGradosDisponibles([]);
      setGradoSel('');
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    cargarGradosRegistrados();
  }, [cargarGradosRegistrados]);

  const cargarPorGrado = useCallback(async (grado) => {
    if (!grado) {
      setGrupo(null);
      setStatus('Activo');
      setAlumnos([]);
      return;
    }
    setCargando(true);
    setGrupo(null);
    setAlumnos([]);
    setInputAlumno('');
    setSugerencias([]);
    try {
      const { data } = await fetchJsonWithFallback(`/grupos/por-grado/${grado}/detalle`);
      if (!data?.grupo) {
        setGrupo(null);
        setStatus('Activo');
        return;
      }

      setGrupo(data.grupo);
      setStatus(data.grupo.Status);
      setAlumnos(
        (data.alumnos || []).map(a => ({
          id: a.IdAlumno,
          nombre: a.NombreCompleto,
          curp: a.CURP
        }))
      );
    } catch (e) {
      console.error('Error al cargar grupo por grado:', e);
    } finally {
      setCargando(false);
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    if (!gradoSel) return;
    cargarPorGrado(gradoSel);
  }, [cargarPorGrado, gradoSel]);

  // ---------- Buscador inteligente (autocomplete) ----------
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!inputAlumno.trim()) { setSugerencias([]); return; }

    debounceRef.current = setTimeout(async () => {
      setLoadingSug(true);
      try {
        const query = `/grupos/buscar-alumnos?q=${encodeURIComponent(inputAlumno.trim())}&limit=15`;
        const { data } = await fetchJsonWithFallback(query);
        setSugerencias(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Error buscando alumnos:', e);
        setSugerencias([]);
      } finally {
        setLoadingSug(false);
      }
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [fetchJsonWithFallback, inputAlumno]);

  function agregarAlumno(item) {
    if (!grupo) return;
    const nuevo = { id: item.IdAlumno, nombre: item.NombreCompleto, curp: item.CURP };
    setAlumnos(prev => (prev.some(x => x.id === nuevo.id) ? prev : [...prev, nuevo]));
    setInputAlumno('');
    setSugerencias([]);
  }
  function quitarAlumno(id) {
    setAlumnos(prev => prev.filter(x => x.id !== id));
  }
  function onEnterAgregarPrimero(e) {
    if (e.key === 'Enter' && sugerencias.length > 0) {
      e.preventDefault();
      agregarAlumno(sugerencias[0]);
    }
  }

  // ---------- Guardar cambios ----------
  const guardarCambios = useCallback(async (e) => {
    e.preventDefault();
    if (!grupo?.IdGrupo) return alert('No hay grupo para este grado');

    const idsAlumnos = alumnos.map(a => a.id);

    try {
      const statusResp = await sendJsonWithFallback(`/grupos/${grupo.IdGrupo}/status`, { status }, 'PUT');
      if (statusResp.parsed?.error) throw new Error(statusResp.parsed.error);

      const alumnosResp = await sendJsonWithFallback(`/grupos/${grupo.IdGrupo}/alumnos`, { idsAlumnos }, 'PUT');
      if (alumnosResp.parsed?.error) throw new Error(alumnosResp.parsed.error);

      alert('✅ Cambios guardados');
      // refrescar por si el server normaliza algo
      cargarPorGrado(gradoSel);
    } catch (e) {
      console.error('Error guardando cambios:', e);
      alert('❌ Error al guardar cambios');
    }
  }, [alumnos, cargarPorGrado, gradoSel, grupo, sendJsonWithFallback, status]);

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="text-center flex-grow-1">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Modificar Grupos</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Tarjeta */}
      <div className="mb-4 p-4 rounded bg-light shadow-sm">
        <h6 className="mb-3">Modificar Grupo</h6>

        {/* Fila fija: Grado selector y datos de grupo (solo lectura) */}
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label">Grado</label>
            <select className="form-select"
              value={gradoSel}
              onChange={(e) => setGradoSel(e.target.value)}
              disabled={gradosDisponibles.length === 0}
            >
              {gradosDisponibles.length === 0 && (
                <option value="" disabled>
                  Sin grupos registrados
                </option>
              )}
              {gradosDisponibles.map((op) => (
                <option key={op.grado} value={op.grado}>
                  {op.etiqueta}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">ID Grupo</label>
            <input className="form-control" value={grupo?.IdGrupo ?? ''} disabled />
          </div>

          <div className="col-md-3">
            <label className="form-label">Grado del Grupo</label>
            <input className="form-control" value={grupo?.Grado ?? ''} disabled />
          </div>

          <div className="col-md-3">
            <label className="form-label">Ciclo Escolar</label>
            <input className="form-control" value={grupo?.CicloEscolar ?? ''} disabled />
          </div>
        </div>

        {/* Editable: Status */}
        <div className="row g-3 mt-3" style={{ display: 'none' }}>
          <div className="col-md-3">
            <label className="form-label">Status</label>
            <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)} disabled={!grupo}>
              <option value="Activo">Activo</option>
              <option value="Baja">Baja</option>
            </select>
          </div>
        </div>

        {/* Agregar alumnos (autocomplete) */}
        <div className="mt-4">
          <label className="form-label">Agregar Alumno</label>
          <div className="position-relative">
            <input
              className="form-control mb-2"
              placeholder="Buscar alumno por nombre o CURP"
              value={inputAlumno}
              onChange={(e) => setInputAlumno(e.target.value)}
              onKeyDown={onEnterAgregarPrimero}
              autoComplete="off"
              disabled={!grupo}
            />
            {grupo && inputAlumno && (loadingSug || sugerencias.length > 0) && (
              <div className="list-group shadow position-absolute w-100" style={{ zIndex: 10, maxHeight: 260, overflowY: 'auto' }}>
                {loadingSug && <div className="list-group-item text-muted">Buscando…</div>}
                {!loadingSug && sugerencias.length === 0 && <div className="list-group-item text-muted">Sin resultados</div>}
                {!loadingSug && sugerencias.map(s => (
                  <button
                    type="button"
                    key={s.IdAlumno}
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    onClick={() => agregarAlumno(s)}
                  >
                    <span>{s.NombreCompleto}</span>
                    <span className="badge bg-secondary">{s.CURP}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tabla de alumnos (quitar/guardar) */}
        <div className="table-responsive mt-3">
          <table className="table table-bordered table-hover shadow-sm bg-white">
            <thead className="table-light">
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th style={{ width: '65%' }}>Nombre del Alumno</th>
                <th>CURP</th>
                <th style={{ width: 120 }}>Quitar</th>
              </tr>
            </thead>
            <tbody>
              {cargando && (
                <tr><td colSpan={4} className="text-center text-muted">Cargando…</td></tr>
              )}
              {!cargando && alumnos.length === 0 && (
                <tr><td colSpan={4} className="text-center text-muted">Sin alumnos en este grupo</td></tr>
              )}
              {!cargando && alumnos.map((a, idx) => (
                <tr key={a.id}>
                  <td>{idx + 1}</td>
                  <td>{a.nombre}</td>
                  <td>{a.curp}</td>
                  <td className="text-center">
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => quitarAlumno(a.id)}>
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Botones */}
        <div className="mt-3 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={guardarCambios} disabled={!grupo}>
            Guardar Cambios
          </button>
          <button className="btn btn-secondary" onClick={(e)=>{e.preventDefault(); if (grupo) cargarPorGrado(gradoSel);}}>
            Cancelar
          </button>
        </div>
      </div>

      {/* Salir */}
      <div className="mt-2 text-center">
        <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
          Salir
        </button>

      </div>
    </div>
  );
}

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}